import React from "react";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import Carousel from "react-material-ui-carousel";
import { BackgroundCircle } from "../../shared/BackgroundCircle";
import { BenefitItem } from "../AccountBenefits";

import iconOpen1 from "../../assets/icon-open-doc-1x.png";
import iconOpen2 from "../../assets/icon-open-doc-2x.png";
import iconOpen3 from "../../assets/icon-open-doc-3x.png";
import iconSearch1 from "../../assets/icon-search-doc-1x.png";
import iconSearch2 from "../../assets/icon-search-doc-2x.png";
import iconSearch3 from "../../assets/icon-search-doc-3x.png";
import iconsGears1 from "../../assets/icon-gears-1x.png";
import iconsGears2 from "../../assets/icon-gears-2x.png";
import iconsGears3 from "../../assets/icon-gears-3x.png";
import { AtticusForm } from "../../../models/AtticusForm";
import { getCarouselStyles } from "../AuthScreen";

const formBenefitItems: BenefitItem[] = [
  {
    imgs: [iconOpen1, iconOpen2, iconOpen3],
    title: "Access your form, instantly",
    desc: "Create an Atticus account and simply select form to print or download",
    styles: {
      position: "relative",
      top: "-2px",
      left: "2px",
    },
  },
  {
    imgs: [iconSearch1, iconSearch2, iconSearch3],
    title: "Over 3,500 court-ready forms",
    desc: "Atticus is the #1 software for free probate forms with the highest quality",
    styles: {
      position: "relative",
      top: "-2px",
      left: "2px",
    },
  },
  {
    imgs: [iconsGears1, iconsGears2, iconsGears3],
    title: "Get a jumpstart",
    desc: "Answer simple questions & we will auto-fill in the right form for you",
  },
];

export const FormsContent = ({ form }: { form?: AtticusForm }): JSX.Element => {
  const theme = useTheme();

  const isNarrow = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const formNumAndTitle = `${form?.governmentId || ""} ${
    form?.formTitle || ""
  }`;
  const stateName = form?.geography?.name;

  const thumbnailAlt = `Thumbnail of probate form ${form?.governmentId}`;
  return (
    <Box
      sx={{
        width: "100%",
        textAlign: "center",
      }}
      className="FormsContent"
    >
      <Box>
        <Typography variant="h4" gutterBottom>
          We’ve got your form!
        </Typography>
        <Typography>
          {stateName ? `${stateName}: ` : ""}
          {formNumAndTitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: isNarrow ? 1 : 5,
          mb: isNarrow ? 3 : 5,
        }}
      >
        <BackgroundCircle
          width={isNarrow ? 160 : 260}
          borderColor={`${theme.palette.success.main}`}
        >
          {form?.s3Urls?.thumbnailFile ? (
            <img
              src={form?.s3Urls?.thumbnailFile}
              alt={thumbnailAlt}
              title={thumbnailAlt}
              style={{
                width: "67%",
                boxShadow:
                  "0px 5px 5px -3px rgba(116, 135, 164, 0.16), 0px 8px 10px 1px rgba(116, 135, 164, 0.11), 0px 3px 14px 2px rgba(116, 135, 164, 0.1)",
              }}
            />
          ) : (
            <Typography sx={{ textAlign: "center" }}>
              {form?.governmentId}
            </Typography>
          )}
        </BackgroundCircle>
      </Box>
      <Carousel
        className="FormsContentCarousel"
        indicators={true}
        autoPlay={true}
        animation="slide"
        height={isNarrow ? 40 : 50}
        sx={{
          mx: isNarrow ? 0 : 4,
        }}
        indicatorContainerProps={
          getCarouselStyles(theme)?.indicatorContainerProps
        }
        indicatorIconButtonProps={
          getCarouselStyles(theme)?.indicatorIconButtonProps
        }
        activeIndicatorIconButtonProps={
          getCarouselStyles(theme)?.activeIndicatorIconButtonProps
        }
      >
        {formBenefitItems?.map((item: BenefitItem) => {
          return (
            <Box
              sx={{
                display: "flex",
                mb: 2,
              }}
              className="form-benefit-item"
              key={item.title}
            >
              <Box
                sx={{
                  mr: 2,
                }}
              >
                <BackgroundCircle
                  width={60}
                  borderColor={`${theme.palette.success.main}`}
                >
                  {item?.imgs?.[0] ? (
                    <img
                      src={item?.imgs?.[0]}
                      srcSet={`${item?.imgs?.[1]} 2x, ${item?.imgs?.[2]} 3x`}
                      alt={item.title}
                      width={34}
                      style={item.styles}
                    />
                  ) : undefined}
                </BackgroundCircle>
              </Box>
              <Box
                sx={{
                  textAlign: "left",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  {item.title}
                </Typography>
                <Typography variant="body2">{item.desc}</Typography>
              </Box>
            </Box>
          );
        })}
      </Carousel>
    </Box>
  );
};
